import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Store, Plus, CheckCircle, Clock, XCircle, ShieldCheck, ShieldAlert, 
  ArrowRight, Loader2, X, Building2, Hash, Car, Wrench, Upload, Image, User, Copy,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import AgentTypeBadge from '../components/AgentTypeBadge';
import {
  DashboardFinancialSummary,
  CommissionChangeBanner,
} from '../components/dashboard/DashboardFinancialSummary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [agentType, setAgentType] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null); // 'pending', 'approved', 'rejected', null
  const [storeStats, setStoreStats] = useState({
    active: 0,
    pending: 0,
    suspended: 0,
    total: 0
  });
  const [recentStores, setRecentStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  
  // Create Store Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newStore, setNewStore] = useState({
    name: '',
    nameEn: '',
    type: '',
    description: '',
    descriptionEn: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Store types (without authorized agent)
  const storeTypes = [
    { id: 'real_estate', name: 'تاجر عقارات', nameEn: 'Real Estate', icon: Building2, color: 'from-blue-500 to-cyan-500', desc: 'بيع وشراء العقارات والأراضي', descEn: 'Buy and sell properties & land' },
    { id: 'phone_numbers', name: 'أرقام الهواتف', nameEn: 'Phone Numbers', icon: Hash, color: 'from-[#1E9BD9] to-[#0D7AB5]', desc: 'أرقام دو، اتصالات، فيرجن المميزة', descEn: 'Du, Etisalat, Virgin premium numbers' },
    { id: 'car_plates', name: 'أرقام السيارات', nameEn: 'Car Plates', icon: Car, color: 'from-amber-500 to-yellow-500', desc: 'لوحات السيارات المميزة', descEn: 'Premium car plate numbers' },
    { id: 'spare_parts', name: 'قطع غيار', nameEn: 'Spare Parts', icon: Wrench, color: 'from-orange-500 to-red-500', desc: 'قطع غيار السيارات والمركبات', descEn: 'Vehicle spare parts' },
    { id: 'auto', name: 'تاجر سيارات', nameEn: 'Auto Dealer', icon: Car, color: 'from-green-500 to-emerald-500', desc: 'بيع وشراء السيارات', descEn: 'Buy and sell vehicles' },
    { id: 'ecommerce', name: 'تجارة إلكترونية', nameEn: 'E-Commerce', icon: Store, color: 'from-cyan-500 to-blue-500', desc: 'عطور، ملابس، إلكترونيات.. إلخ', descEn: 'Perfumes, clothes, electronics, etc.' }
  ];

  // Validation functions
  const isArabicOnly = (text) => {
    const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s\d.,!?()-]+$/;
    return arabicRegex.test(text) || text === '';
  };

  const isEnglishOnly = (text) => {
    const englishRegex = /^[a-zA-Z\s\d.,!?()-]+$/;
    return englishRegex.test(text) || text === '';
  };

  const handleArabicInput = (field, value) => {
    if (isArabicOnly(value)) {
      setNewStore({ ...newStore, [field]: value });
    } else {
      toast.error(language === 'ar' ? 'يرجى إدخال نص عربي فقط' : 'Please enter Arabic text only');
    }
  };

  const handleEnglishInput = (field, value) => {
    if (isEnglishOnly(value)) {
      setNewStore({ ...newStore, [field]: value });
    } else {
      toast.error(language === 'ar' ? 'يرجى إدخال نص إنجليزي فقط' : 'Please enter English text only');
    }
  };

  // Logo upload handler
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem('business_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setIsVerified(parsedUser.account_verified || false);
    }
    fetchDashboardData();
    fetchUserProfile();
    fetchAgentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch agent status
  const fetchAgentStatus = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.length > 0) {
        // Check if ANY request is approved (not just the latest)
        const approvedRequest = response.data.find(req => req.status === 'approved');
        if (approvedRequest) {
          setAgentStatus('approved');
          setIsAgent(true);
          setAgentType(approvedRequest.agent_type || null);
        } else {
          // If no approved, get the latest request status
          const latestRequest = response.data[0];
          setAgentStatus(latestRequest.status);
          setIsAgent(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch agent status:', error);
    }
  };

  const fetchUserProfile = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const copyBGMNumber = () => {
    if (userProfile?.bgm_number) {
      navigator.clipboard.writeText(userProfile.bgm_number);
      toast.success(language === 'ar' ? 'تم نسخ الرقم' : 'Number copied');
    }
  };

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoreStats(response.data.stats);
      setRecentStores(response.data.recent_stores || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createStore = async () => {
    if (!newStore.name || !newStore.type) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    const token = localStorage.getItem('business_token');
    setCreating(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', newStore.name);
      formData.append('nameEn', newStore.nameEn);
      formData.append('type', newStore.type);
      formData.append('description', newStore.description);
      formData.append('descriptionEn', newStore.descriptionEn);
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await axios.post(`${BACKEND_URL}/api/stores`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refresh dashboard data
      await fetchDashboardData();
      
      setShowCreateModal(false);
      setNewStore({ name: '', nameEn: '', type: '', description: '', descriptionEn: '' });
      setLogoFile(null);
      setLogoPreview(null);
      toast.success(language === 'ar' ? 'تم إنشاء المتجر بنجاح! سيتم مراجعته من قبل الإدارة.' : 'Store created successfully! It will be reviewed by admin.');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إنشاء المتجر' : 'Failed to create store'));
    } finally {
      setCreating(false);
    }
  };

  const stats = [
    { 
      Icon: CheckCircle, 
      label: 'المتاجر الفعالة', 
      labelEn: 'Active Stores', 
      value: storeStats.active, 
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    { 
      Icon: Clock, 
      label: 'قيد المراجعة', 
      labelEn: 'Pending Review', 
      value: storeStats.pending, 
      color: 'from-[#F5A962] to-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    },
    { 
      Icon: XCircle, 
      label: 'المتاجر المعلقة', 
      labelEn: 'Suspended', 
      value: storeStats.suspended, 
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600'
    }
  ];

  if (!user) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="text-center">
          <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{language === 'ar' ? 'يجب تسجيل الدخول أولاً' : 'Please login first'}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#38BDF8]/20 border-t-[#38BDF8] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden cgm-page`}>
      {/* Header Section — AI-Grade Animated Hero */}
      <section className="cgm-hero py-6 sm:py-8">
        <div className="cgm-hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-white">
            <div className="cgm-reveal">
              <div className="flex items-center gap-3 mb-2">
                <div className="cgm-icon-glow cgm-breathe !p-2.5">
                  <User className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="dashboard-title">
                  {language === 'ar' ? `مرحباً ${user.name}!` : `Welcome ${user.name}!`}
                </h1>
              </div>
              <p className="text-white/80 text-sm sm:text-base">{language === 'ar' ? 'لوحة التحكم الذكية الخاصة بك' : 'Your intelligent control center'}</p>
              {userProfile?.bgm_number && (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-white/70">{language === 'ar' ? 'رقم التاجر:' : 'Business ID:'}</span>
                  <button 
                    onClick={copyBGMNumber}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 backdrop-blur-md bg-white/15 hover:bg-white/25 border border-white/20"
                    data-testid="bgm-number"
                  >
                    <span className="font-mono font-bold text-sm">{userProfile.bgm_number}</span>
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            <button 
              onClick={() => navigate('/stores/create')}
              className="cgm-cta-primary w-full sm:w-auto cgm-reveal !bg-white !text-slate-900 !shadow-xl hover:!bg-slate-50"
              style={{ animationDelay: '150ms', background: '#ffffff' }}
              data-testid="go-to-stores-btn"
            >
              <Plus className="h-5 w-5 text-[#F97316]" />
              <span className="font-black" style={{ background: 'linear-gradient(90deg, #0EA5E9, #F97316)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                {language === 'ar' ? 'إنشاء متجر جديد' : 'Create New Store'}
              </span>
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* iter-294 — Commission change flash banner (fires once per change) */}
        <CommissionChangeBanner language={language} isDarkMode={isDarkMode} />

        {/* Account Verification Card */}
        <div className={`mb-6 sm:mb-8 cgm-ai-card cgm-reveal ${
          isVerified 
            ? isDarkMode 
              ? '!bg-emerald-500/5 !border-emerald-500/30' 
              : '!bg-gradient-to-r !from-emerald-50 !to-teal-50 !border-emerald-200'
            : isDarkMode 
              ? '!bg-orange-500/5 !border-orange-500/30' 
              : '!bg-gradient-to-r !from-orange-50 !to-amber-50 !border-orange-200'
        }`} data-testid="verification-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {isVerified ? (
                <div className="cgm-icon-glow !p-3 !bg-gradient-to-br !from-emerald-500 !to-teal-600 cgm-breathe" style={{ animationName: 'cgm-breathe', filter: 'hue-rotate(80deg)' }}>
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
              ) : (
                <div className="cgm-icon-glow !p-3 !bg-gradient-to-br !from-orange-500 !to-amber-500 cgm-breathe">
                  <ShieldAlert className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <h3 className={`text-lg sm:text-xl font-black ${
                  isVerified 
                    ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700') 
                    : (isDarkMode ? 'text-orange-400' : 'text-orange-700')
                }`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {isVerified 
                    ? (language === 'ar' ? 'حسابك موثق' : 'Account Verified')
                    : (language === 'ar' ? 'حسابك غير موثق' : 'Account Not Verified')
                  }
                </h3>
                <p className={`text-sm ${
                  isVerified 
                    ? (isDarkMode ? 'text-emerald-300/70' : 'text-emerald-600') 
                    : (isDarkMode ? 'text-orange-300/70' : 'text-orange-600')
                }`}>
                  {isVerified 
                    ? (language === 'ar' ? 'تمتع بجميع المميزات' : 'Enjoy all features')
                    : (language === 'ar' ? 'وثق حسابك للحصول على مميزات إضافية' : 'Verify your account for additional features')
                  }
                </p>
                {!isVerified && (
                  <div className="flex items-center gap-2 mt-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_cloudgm-hub-1/artifacts/bbxqrxwz_verified.png" 
                      alt="Verified Badge" 
                      className="h-5 w-5"
                    />
                    <span className="text-xs text-[#38BDF8] font-semibold">
                      {language === 'ar' ? 'احصل على العلامة الزرقاء بعد التوثيق' : 'Get the blue badge after verification'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              {!isVerified && (
                <Link to="/verification" className="flex-1 sm:flex-none">
                  <button className="cgm-cta-primary w-full" data-testid="verify-account-btn">
                    {language === 'ar' ? 'توثيق الحساب' : 'Verify Account'}
                    <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Agent Section - Dynamic based on status */}
        {isAgent ? (
          // Approved Agent - Show Agent Dashboard Button
          <div className={`mb-6 sm:mb-8 cgm-ai-card cgm-reveal ${
            isDarkMode 
              ? '!bg-emerald-500/5 !border-emerald-500/30' 
              : '!bg-gradient-to-r !from-emerald-50 !to-teal-50 !border-emerald-200'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="cgm-icon-glow !p-3 !bg-gradient-to-br !from-emerald-500 !to-teal-600">
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className={`text-lg sm:text-xl font-black flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                    {language === 'ar' ? 'أنت وكيل معتمد' : 'You are an Authorized Agent'}
                    <CheckCircle className="h-5 w-5" />
                    <AgentTypeBadge agentType={agentType} language={language} size="sm" />
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-emerald-300/70' : 'text-emerald-600'}`}>
                    {language === 'ar' ? 'يمكنك الآن الوصول لمهام الوكيل الحصرية' : 'You can now access exclusive agent tasks'}
                  </p>
                </div>
              </div>
              <Link to="/agent-tasks">
                <button className="cgm-cta-primary" data-testid="agent-tasks-btn">
                  <Briefcase className="h-5 w-5" />
                  {language === 'ar' ? 'مهام الوكيل' : 'Agent Tasks'}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
              </Link>
            </div>
          </div>
        ) : agentStatus === 'pending' ? (
          // Pending Agent Request
          <div className={`mb-6 sm:mb-8 cgm-ai-card cgm-reveal ${
            isDarkMode 
              ? '!bg-amber-500/5 !border-amber-500/30' 
              : '!bg-gradient-to-r !from-amber-50 !to-orange-50 !border-amber-200'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="cgm-icon-glow !p-3 !bg-gradient-to-br !from-amber-500 !to-orange-500 cgm-breathe">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className={`text-lg sm:text-xl font-black ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                    {language === 'ar' ? 'طلبك قيد المراجعة' : 'Your Request is Under Review'}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                    {language === 'ar' ? 'سيتم إشعارك عند الموافقة على طلب الوكيل' : 'You will be notified when your agent request is approved'}
                  </p>
                </div>
              </div>
              <Link to="/become-agent">
                <button className="cgm-btn-secondary" data-testid="view-request-btn">
                  {language === 'ar' ? 'عرض الطلب' : 'View Request'}
                </button>
              </Link>
            </div>
          </div>
        ) : (
          // Not an Agent - Show Registration CTA
          <div className={`mb-6 sm:mb-8 cgm-ai-card-featured cgm-reveal`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="cgm-icon-glow !p-3 cgm-breathe">
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    {language === 'ar' ? 'كن وكيلاً معتمداً لـ CloudGM' : 'Become a CloudGM Authorized Agent'}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {language === 'ar' ? 'انضم لفريق الوكلاء المعتمدين واحصل على مميزات حصرية' : 'Join our authorized agents team and get exclusive benefits'}
                  </p>
                </div>
              </div>
              <Link to="/become-agent">
                <button className="cgm-cta-primary" data-testid="become-agent-btn">
                  {language === 'ar' ? 'سجل كوكيل' : 'Register as Agent'}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
              </Link>
            </div>
          </div>
        )}

        {/* Store Statistics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 cgm-reveal-stagger">
          {stats.map((stat, index) => (
            <div key={index} className="cgm-ai-card group" data-testid={`stat-${index}`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`cgm-icon-glow !p-2.5 bg-gradient-to-br ${stat.color} group-hover:scale-110 transition-transform duration-500`}>
                  <stat.Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="cgm-stat-value-ai">{stat.value}</span>
              </div>
              <h3 className={`text-sm sm:text-base font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                {language === 'ar' ? stat.label : stat.labelEn}
              </h3>
            </div>
          ))}
        </div>

        {/* iter-294 — Combined Financial Movement across all selling stores */}
        <DashboardFinancialSummary language={language} isDarkMode={isDarkMode} />

        {/* Empty State */}
        {storeStats.total === 0 && (
          <div className="cgm-ai-card cgm-reveal text-center py-16">
            <div className="cgm-icon-glow !w-20 !h-20 !p-5 inline-flex mb-6 cgm-float">
              <Store className="h-10 w-10 text-white" />
            </div>
            <p className="text-xl font-black mb-2 cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {language === 'ar' ? 'لم تنشئ أي متجر بعد' : "You haven't created any stores yet"}
            </p>
            <p className={`mb-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {language === 'ar' ? 'ابدأ بإنشاء متجرك الأول الآن' : 'Start by creating your first store now'}
            </p>
            <button 
              onClick={() => navigate('/stores/create')}
              className="cgm-cta-primary mx-auto"
            >
              <Plus className="h-5 w-5" />
              {language === 'ar' ? 'إنشاء متجرك الأول' : 'Create Your First Store'}
            </button>
          </div>
        )}
      </section>

      {/* Create Store Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold font-heading">
                {language === 'ar' ? 'إنشاء متجر جديد' : 'Create New Store'}
              </h2>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  setNewStore({ name: '', nameEn: '', type: '', description: '', descriptionEn: '' });
                  setLogoFile(null);
                  setLogoPreview(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Logo Upload */}
              <div>
                <Label className="text-gray-700 font-medium mb-3 block">
                  {language === 'ar' ? 'شعار المتجر' : 'Store Logo'}
                </Label>
                <div className="flex items-center gap-4">
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#1E9BD9] transition-colors overflow-hidden"
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Image className="h-8 w-8 mx-auto text-gray-400" />
                        <p className="text-xs text-gray-400 mt-1">{language === 'ar' ? 'رفع' : 'Upload'}</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">
                      {language === 'ar' ? 'اضغط لرفع شعار متجرك' : 'Click to upload your store logo'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {language === 'ar' ? 'PNG, JPG حتى 5 ميجابايت' : 'PNG, JPG up to 5MB'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Store Type Selection */}
              <div>
                <Label className="text-gray-700 font-medium mb-3 block">
                  {language === 'ar' ? 'نوع المتجر *' : 'Store Type *'}
                </Label>
                <div className="grid grid-cols-1 gap-3">
                  {storeTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setNewStore({ ...newStore, type: type.id })}
                      className={`p-4 rounded-xl border-2 transition-all text-start flex items-center gap-4 ${
                        newStore.type === type.id 
                          ? 'border-[#1E9BD9] bg-[#1E9BD9]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${type.color} text-white`}>
                        <type.icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${newStore.type === type.id ? 'text-[#1E9BD9]' : 'text-[#0F172A]'}`}>
                          {language === 'ar' ? type.name : type.nameEn}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {language === 'ar' ? type.desc : type.descEn}
                        </p>
                      </div>
                      {newStore.type === type.id && (
                        <CheckCircle className="h-5 w-5 text-[#1E9BD9]" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Store Name Arabic */}
              <div>
                <Label className="text-gray-700 font-medium">
                  {language === 'ar' ? 'اسم المتجر (عربي) *' : 'Store Name (Arabic) *'}
                </Label>
                <Input
                  placeholder={language === 'ar' ? 'مثال: متجر الأرقام الذهبية' : 'e.g., متجر الأرقام الذهبية'}
                  value={newStore.name}
                  onChange={(e) => handleArabicInput('name', e.target.value)}
                  className="mt-2"
                  dir="rtl"
                  data-testid="store-name-input"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {language === 'ar' ? 'يقبل النص العربي فقط' : 'Arabic text only'}
                </p>
              </div>

              {/* Store Name English */}
              <div>
                <Label className="text-gray-700 font-medium">
                  {language === 'ar' ? 'اسم المتجر (إنجليزي) *' : 'Store Name (English) *'}
                </Label>
                <Input
                  placeholder="e.g., Golden Numbers Store"
                  value={newStore.nameEn}
                  onChange={(e) => handleEnglishInput('nameEn', e.target.value)}
                  className="mt-2"
                  dir="ltr"
                  data-testid="store-name-en-input"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {language === 'ar' ? 'يقبل النص الإنجليزي فقط' : 'English text only'}
                </p>
              </div>

              {/* Description Arabic */}
              <div>
                <Label className="text-gray-700 font-medium">
                  {language === 'ar' ? 'وصف المتجر (عربي)' : 'Store Description (Arabic)'}
                </Label>
                <textarea
                  placeholder={language === 'ar' ? 'وصف قصير عن متجرك بالعربي...' : 'Brief description in Arabic...'}
                  value={newStore.description}
                  onChange={(e) => handleArabicInput('description', e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 focus:border-[#1E9BD9]"
                  dir="rtl"
                  data-testid="store-description-input"
                />
              </div>

              {/* Description English */}
              <div>
                <Label className="text-gray-700 font-medium">
                  {language === 'ar' ? 'وصف المتجر (إنجليزي)' : 'Store Description (English)'}
                </Label>
                <textarea
                  placeholder="Brief description in English..."
                  value={newStore.descriptionEn}
                  onChange={(e) => handleEnglishInput('descriptionEn', e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 focus:border-[#1E9BD9]"
                  dir="ltr"
                  data-testid="store-description-en-input"
                />
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewStore({ name: '', nameEn: '', type: '', description: '', descriptionEn: '' });
                  setLogoFile(null);
                  setLogoPreview(null);
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={createStore}
                disabled={creating || !newStore.name || !newStore.type || !newStore.nameEn}
                className="flex-1 bg-[#1E9BD9] hover:bg-[#0369A1] text-white"
                data-testid="submit-store-btn"
              >
                {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'إنشاء المتجر' : 'Create Store')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
